import React, { useState } from 'react';
import { useReducedMotion } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * MoodGarden — a little row of flowers. Tap one and it blooms: its petals spring
 * open and a few sparkles rise. Tap again to let it close. Purely for enjoyment —
 * no score, no completion. Flowers are buttons (keyboard-bloomable). Under reduced
 * motion the bloom is instant.
 */
const FLOWERS = [
  { id: 'f1', x: 12, hue: 44, scale: 1 },
  { id: 'f2', x: 31, hue: 172, scale: 0.86 },
  { id: 'f3', x: 50, hue: 320, scale: 1.08 },
  { id: 'f4', x: 69, hue: 262, scale: 0.92 },
  { id: 'f5', x: 87, hue: 12, scale: 1 },
];

const Flower: React.FC<{ hue: number; bloomed: boolean }> = ({ hue, bloomed }) => (
  <svg viewBox="0 0 60 90" className={`mm-flower__svg ${bloomed ? 'is-bloomed' : ''}`}>
    <path className="mm-flower__stem" d="M30 90 C 30 70, 26 60, 30 48" stroke={`hsl(150 45% 42%)`} strokeWidth="3" fill="none" />
    <path className="mm-flower__leaf" d="M30 70 C 20 66, 16 72, 22 78 C 28 76, 30 74, 30 70 Z" fill="hsl(150 45% 48%)" />
    <g className="mm-flower__petals" style={{ transformOrigin: '30px 34px' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <ellipse key={i} cx="30" cy="20" rx="7" ry="13" fill={`hsl(${hue} 75% 66%)`} transform={`rotate(${i * 60} 30 34)`} opacity="0.92" />
      ))}
      <circle cx="30" cy="34" r="7" fill={`hsl(${(hue + 30) % 360} 80% 72%)`} />
    </g>
  </svg>
);

export const MoodGarden: React.FC = () => {
  const reduced = useReducedMotion();
  const [bloomed, setBloomed] = useState<Record<string, boolean>>({});
  const { ripples, spawnAtElement } = useRipples();

  const toggle = (id: string, el: HTMLElement) => {
    setBloomed((p) => ({ ...p, [id]: !p[id] }));
    if (!bloomed[id]) spawnAtElement(el);
  };

  return (
    <div className={`mm-world mm-world--garden ${reduced ? 'is-still' : ''}`}>
      <div className="mm-world__ground" aria-hidden="true" />
      {FLOWERS.map((f) => (
        <button
          key={f.id}
          type="button"
          className="mm-flower"
          aria-label={bloomed[f.id] ? 'Close flower' : 'Bloom flower'}
          onClick={(e) => toggle(f.id, e.currentTarget)}
          style={{ left: `${f.x}%`, ['--fscale' as string]: f.scale }}
        >
          <Flower hue={f.hue} bloomed={!!bloomed[f.id]} />
        </button>
      ))}
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Tap a flower to watch it bloom 🌸</p>
    </div>
  );
};

export default MoodGarden;
